<template>
  <div class="p-realtime-stocks-section">
    <div class="section-placeholder" v-if="isLoading">
      <span class="adn-spinner adn-spinner-primary"></span>
      <h4>{{ i18n._('[context/realtime-stocks] Chargement des stocks en cours') }}</h4>
    </div>
    <div class="variant-rows" v-if="!isLoading">
      <div v-if="hasSilo">
        <div class="variant-row" v-for="variant in variantStocksInfos" :key="variant.id">
          <div class="variant-row__header">
            <span class="variant-name">{{ variant.name }}</span>
            <adn-button
              v-if="variant.hasOffers"
              @click="$emit('shortcut', variant)"
              class="shortcut"
              kind="link"
              icon-name="navigation-arrow-right"
            />
          </div>
          <div class="variant-row__body">
            <dl class="stocks-table">
              <dt>{{ i18n._('[context/realtime-stocks] Total') }}</dt>
              <dd :class="{ 'quantity-highlighted': variant.totals.available.isHighlighted }">
                {{ variant.totals.available.label }}
              </dd>
              <template v-for="stock in variant.stocks" :key="stock.warehouseId">
                <dt>{{ stock.warehouseName }}</dt>
                <dd :class="{ 'quantity-highlighted': stock.available.isHighlighted }">
                  {{ stock.available.label }}
                </dd>
              </template>
            </dl>
          </div>
        </div>
      </div>
      <div v-else>
        <div class="section-placeholder-error">
          <span class="adn-icon adn-icon--status-deleted" />
          <span>{{ i18n._('[context/realtime-stocks] Aucun silo') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { type PropType, computed } from 'vue'
import { i18n } from '@b2ag/locale'
import type { VariantStocksInfo } from './presentation-models'
import AdnButton from '@b2ag/design-system/src/components/AdnButton/AdnButton.vue'

const props = defineProps({
  variantStocksInfos: {
    type: Array as PropType<VariantStocksInfo[]>,
    required: true,
  },
  isLoading: {
    type: Boolean,
    required: true,
  },
})

defineEmits({ shortcut: (_payload: VariantStocksInfo) => true })

const hasSilo = computed(() => props.variantStocksInfos.some((variant) => variant.stocks.length > 0))
</script>

<style scoped lang="scss">
.p-realtime-stocks-section {
  display: flex;
  flex-direction: column;
  padding: var(--spacing-3) var(--spacing-3);
  gap: var(--spacing-2);

  @media (min-width: $breakpoint-md) {
    padding: var(--spacing-4) 0 var(--spacing-3) 0;
  }

  .section-placeholder {
    gap: var(--spacing-2);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--spacing-2);
    background: white;
    border-radius: var(--border-radius-2);
    border: 1px solid var(--color-border);

    h4 {
      margin: 0;
      font-size: var(--font-size-m);
      line-height: var(--line-height-m);
      color: --color-text;
    }
  }

  .section-placeholder-error {
    gap: var(--spacing-2);
    display: flex;
    align-items: center;
    padding: var(--spacing-2);
    background: white;
    border-left: 5px solid red;

    .adn-icon--status-deleted {
      color: red;
    }

    h4 {
      margin: 0;
      font-size: var(--font-size-m);
      line-height: var(--line-height-m);
      color: --color-text;
    }
  }

  .variant-rows {
    border: 1px solid var(--color-border);
    border-radius: var(--border-radius-2);
    overflow: hidden;
  }

  .variant-row {
    .variant-row__header {
      display: flex;
      flex-direction: row;
      place-content: center space-between;
      padding: var(--spacing-2);
      background: linear-gradient(90deg, var(--gradient-main-start), var(--gradient-main-end));

      .variant-name {
        font-weight: 700;
        font-size: var(--font-size-m);
        color: white;
        line-height: var(--line-height-m);
      }

      .shortcut {
        min-height: 0;
        line-height: 0;
        padding: 0;
        color: white;
      }
    }

    .variant-row__body {
      .stocks-table {
        width: 100%;
        margin: 0;
        display: grid;
        grid-template-columns: 1fr max-content;
        grid-template-rows: 1fr;
        background-color: var(--color-background-light);

        dt,
        dd {
          padding: var(--spacing-1) var(--spacing-2);
          margin: 0;
          font-weight: 400;
          font-size: var(--font-size-m);
          color: var(--color-text);
          line-height: var(--line-height-m);
          white-space: nowrap;

          &:first-of-type {
            background-color: var(--color-grey-lighter);
            font-weight: 700;
          }
        }

        dt {
          text-overflow: ellipsis;
          overflow: hidden;
        }

        dd {
          text-align: end;
        }

        .quantity-highlighted {
          color: var(--color-error);
        }
      }
    }
  }
}
</style>
